import React, { useState, useEffect, useCallback } from 'react';
import { useOrg } from '../context/OrgContext';
import { usePermissions } from '../components/PermissionGuard';
import { PageHeader } from '../components/PageHeader';
import * as reportTypesApi from '../api/reportTypes';

export function ReportingStudio() {
  const { selectedOrg } = useOrg();
  const { canReadAssets } = usePermissions();
  const [reportTypes, setReportTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState('');
  const [selectedReportType, setSelectedReportType] = useState(null);

  const loadReportTypes = useCallback(async () => {
    if (!selectedOrg || !canReadAssets) {
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const filters = {};
      if (selectedCategory) filters.category = selectedCategory;
      const data = await reportTypesApi.getReportTypes(selectedOrg.id, filters);
      setReportTypes(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [selectedOrg, canReadAssets, selectedCategory]);

  useEffect(() => {
    loadReportTypes();
  }, [loadReportTypes]);

  const handleViewDetails = (reportType) => {
    setSelectedReportType(reportType);
  };

  if (!canReadAssets) {
    return (
      <div style={styles.container}>
        <div style={styles.accessDenied}>
          <h2>Access Denied</h2>
          <p>You do not have permission to view report types.</p>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <PageHeader
        title="Reporting Studio"
        subtitle="Available report types and templates"
      />

      <div style={styles.toolbar}>
        <select
          value={selectedCategory}
          onChange={(e) => setSelectedCategory(e.target.value)}
          style={styles.select}
          data-testid="category-filter"
        >
          <option value="">All Categories</option>
          <option value="utility">Utility</option>
          <option value="operations">Operations</option>
          <option value="financial">Financial</option>
          <option value="sustainability">Sustainability</option>
          <option value="compliance">Compliance</option>
        </select>
      </div>

      {error && (
        <div style={styles.error}>{error}</div>
      )}

      {loading ? (
        <div style={styles.loading}>Loading report types...</div>
      ) : (
        <div style={styles.grid} data-testid="report-types-grid">
          {reportTypes.map(rt => (
            <div 
              key={rt.id} 
              style={styles.card}
              onClick={() => handleViewDetails(rt)}
              data-testid={`report-type-${rt.id}`}
            >
              <div style={styles.cardHeader}>
                <span style={styles.categoryBadge}>{rt.category}</span>
              </div>
              <h3 style={styles.cardTitle}>{rt.name}</h3>
              <p style={styles.cardDescription}>{rt.description}</p>
              <div style={styles.cardMeta}>
                <div style={styles.metaItem}>
                  <span style={styles.metaLabel}>Datasets:</span>
                  <span>{rt.requiredDatasets.length}</span>
                </div>
                <div style={styles.metaItem}>
                  <span style={styles.metaLabel}>Formats:</span>
                  <span>{rt.outputFormats.join(', ')}</span>
                </div>
              </div>
            </div>
          ))}
          {reportTypes.length === 0 && (
            <div style={styles.emptyState}>No report types found</div>
          )}
        </div>
      )}

      {selectedReportType && (
        <div style={styles.modalOverlay}>
          <div style={styles.modal} data-testid="report-type-modal">
            <h3 style={styles.modalTitle}>{selectedReportType.name}</h3>
            <p style={styles.modalDescription}>{selectedReportType.description}</p>
            
            <h4 style={styles.sectionTitle}>Required Datasets</h4>
            <div style={styles.datasetList}>
              {selectedReportType.requiredDatasets.map(ds => (
                <span key={ds} style={styles.datasetBadge}>{ds}</span>
              ))}
            </div>
            
            <h4 style={styles.sectionTitle}>Default Validations</h4>
            <table style={styles.validationsTable}>
              <thead>
                <tr>
                  <th style={styles.th}>Field</th>
                  <th style={styles.th}>Rule</th>
                  <th style={styles.th}>Value</th>
                </tr>
              </thead>
              <tbody>
                {selectedReportType.defaultValidations.map((v, i) => (
                  <tr key={i} style={styles.tr}>
                    <td style={styles.td}>{v.field}</td>
                    <td style={styles.td}>{v.rule}</td>
                    <td style={styles.td}>{v.value !== undefined ? v.value : '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            
            <h4 style={styles.sectionTitle}>Output Formats</h4>
            <div style={styles.formatList}>
              {selectedReportType.outputFormats.map(fmt => (
                <span key={fmt} style={styles.formatBadge}>{fmt.toUpperCase()}</span>
              ))}
            </div>
            
            <div style={styles.modalActions}>
              <button
                onClick={() => setSelectedReportType(null)}
                style={styles.closeButton}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const styles = {
  container: {
    padding: '2rem',
    maxWidth: '1400px',
    margin: '0 auto'
  },
  accessDenied: {
    padding: '3rem',
    textAlign: 'center',
    background: '#fff',
    borderRadius: '8px',
    boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
  },
  toolbar: {
    marginBottom: '1.5rem'
  },
  select: {
    padding: '0.5rem 1rem',
    borderRadius: '6px',
    border: '1px solid #e2e8f0',
    fontSize: '0.875rem',
    background: 'white'
  },
  error: {
    padding: '1rem',
    background: '#fed7d7',
    color: '#c53030',
    borderRadius: '8px',
    marginBottom: '1rem'
  },
  loading: {
    padding: '2rem',
    textAlign: 'center',
    color: '#718096'
  },
  grid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
    gap: '1.5rem'
  },
  card: {
    background: 'white',
    borderRadius: '8px',
    padding: '1.5rem',
    boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
    cursor: 'pointer',
    transition: 'box-shadow 0.15s'
  },
  cardHeader: {
    marginBottom: '0.75rem'
  },
  categoryBadge: {
    padding: '0.25rem 0.5rem',
    background: '#e2e8f0',
    borderRadius: '4px',
    fontSize: '0.75rem',
    textTransform: 'capitalize'
  },
  cardTitle: {
    margin: '0 0 0.5rem 0',
    fontSize: '1.125rem',
    fontWeight: '600',
    color: '#2d3748'
  },
  cardDescription: {
    margin: '0 0 1rem 0',
    fontSize: '0.875rem',
    color: '#718096',
    lineHeight: '1.5'
  },
  cardMeta: {
    display: 'flex',
    gap: '1rem',
    fontSize: '0.75rem',
    color: '#a0aec0'
  },
  metaItem: {
    display: 'flex',
    gap: '0.25rem'
  },
  metaLabel: {
    fontWeight: '500'
  },
  emptyState: {
    gridColumn: '1 / -1',
    padding: '2rem',
    textAlign: 'center',
    color: '#718096'
  },
  modalOverlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'rgba(0,0,0,0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000
  },
  modal: {
    background: 'white',
    borderRadius: '8px',
    padding: '1.5rem',
    minWidth: '500px',
    maxWidth: '600px',
    maxHeight: '80vh',
    overflow: 'auto',
    boxShadow: '0 4px 6px rgba(0,0,0,0.1)'
  },
  modalTitle: {
    margin: '0 0 0.5rem 0',
    fontSize: '1.25rem',
    fontWeight: '600'
  },
  modalDescription: {
    margin: '0 0 1.5rem 0',
    color: '#718096',
    fontSize: '0.875rem'
  },
  sectionTitle: {
    margin: '1.5rem 0 0.75rem 0',
    fontSize: '0.875rem',
    fontWeight: '600',
    color: '#4a5568'
  },
  datasetList: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '0.5rem'
  },
  datasetBadge: {
    padding: '0.25rem 0.5rem',
    background: '#bee3f8',
    color: '#2b6cb0',
    borderRadius: '4px',
    fontSize: '0.75rem'
  },
  validationsTable: {
    width: '100%',
    borderCollapse: 'collapse',
    fontSize: '0.875rem'
  },
  th: {
    textAlign: 'left',
    padding: '0.5rem',
    background: '#f7fafc',
    borderBottom: '1px solid #e2e8f0',
    fontWeight: '600',
    fontSize: '0.75rem',
    color: '#4a5568'
  },
  tr: {
    borderBottom: '1px solid #e2e8f0'
  },
  td: {
    padding: '0.5rem',
    fontSize: '0.75rem'
  },
  formatList: {
    display: 'flex',
    gap: '0.5rem'
  },
  formatBadge: {
    padding: '0.25rem 0.5rem',
    background: '#c6f6d5',
    color: '#276749',
    borderRadius: '4px',
    fontSize: '0.75rem',
    fontWeight: '500'
  },
  modalActions: {
    display: 'flex',
    justifyContent: 'flex-end',
    marginTop: '1.5rem'
  },
  closeButton: {
    padding: '0.5rem 1rem',
    borderRadius: '6px',
    border: '1px solid #e2e8f0',
    background: 'white',
    fontSize: '0.875rem',
    cursor: 'pointer'
  }
};
